// eslint-disable-next-line import/named
import '../../conversation/support-ticket-actions';
import {
  BranchQuote,
  BranchQuoteSupport,
  BranchQuoteSupportItemType,
  BranchQuoteSupportStatus
} from '../../../api/dealer-api-interface-franchisee';
import { customElement } from 'lit/decorators.js';
import { emptyGuid, newGuid } from '../../../../webmodule-common/other/api/guid';
import { EventTemplate } from '../../../../webmodule-common/other/ui/events';
import { FranchiseeQuoteContainerManager } from '../data/franchisee-quote-manager';
import { getApiFactory } from '../../../api/api-injector';
import { html } from 'lit';
import { isEmptyOrSpace } from '../../../../webmodule-common/other/ui/string-helper-functions';
import { LangBranchQuoteSupportNew } from '../../../../localization/scripts/dealer-franchisee/conversation/wm-branchquote-support-new-lang';
import { LangFranchiseeQuoteSupportTicketView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-support-ticket-editor-view-lang';
import { lockUIandExecute } from '../../../../webmodule-common/other/ui-lock';
import { PageControlTabWithIndependantSaving } from '../../../../webmodule-common/other/ui/data-entry-screen-base';
import { showValidations } from '../../../../webmodule-common/other/ui/modal-validationhandler';
import { Snippet } from '../../../../webmodule-common/interop/webmodule-interop';
import {
  SupportTicketsDelta,
  SupportTicketService,
  SupportTicketsState
} from '../../conversation/support-ticket-service';
import { WebModuleBranchQuoteConversationEditor } from '../../conversation/wm-branchquote-support-editor';
import { WebModuleBranchQuoteSupportNew } from '../../conversation/wm-branchquote-support-new';

@customElement('wm-franchiseequotesupportticketview')
export class FranchiseeQuoteSupportTicketView extends PageControlTabWithIndependantSaving {
  private addRequestType: BranchQuoteSupportItemType;
  private _branchQuoteSupport?: BranchQuoteSupport | undefined;
  // Track current draft ticket id while creating a new ticket (session-centric model)
  private _draftTicketId: string | null = null;
  public get branchQuoteSupport(): BranchQuoteSupport | undefined {
    return this._branchQuoteSupport;
  }
  public set branchQuoteSupport(value: BranchQuoteSupport | undefined) {
    this._branchQuoteSupport = value;
    this.requestUpdate();
  }
  protected dataPrimaryKey: string = newGuid();
  protected quoteManager: FranchiseeQuoteContainerManager;
  protected franchiseeApi = getApiFactory().franchisee();
  constructor(
    quoteManager: FranchiseeQuoteContainerManager,
    branchQuoteSupport?: BranchQuoteSupport,
    addRequestType?: BranchQuoteSupportItemType
  ) {
    super();
    this.quoteManager = quoteManager;
    this.branchQuoteSupport = branchQuoteSupport;
    this.addRequestType = addRequestType ?? BranchQuoteSupportItemType.QuoteAssistanceRequest;
  }
  async afterConstruction(): Promise<void> {
    await super.afterConstruction();
    // Perform all hydration & one-time conversation load here (post-construction lifecycle hook)
    const svc = this.quoteManager.supportTicketService;
    if (this.branchQuoteSupport && !this.isNew()) {
      await svc.openTicket(this.branchQuoteSupport.id, { edit: true }); // allow error to propagate
      this._didInitialConversationLoad = true;
    } else if (this.isNew()) {
      // Initialize draft session immediately (new API)
      const draftId = svc.createSupportTicketDraft({
        quoteId: this.quoteManager.quoteId,
        supportType: this.addRequestType,
        supplierName: this.quoteManager.supplierName
      });
      this._draftTicketId = draftId;
    }
  }
  private _unsubscribe?: () => void;
  connectedCallback(): void {
    super.connectedCallback();
    const svc = this.quoteManager.supportTicketService;
    this._subscribeSnapshots(svc);
  }
  disconnectedCallback(): void {
    if (this.branchQuoteSupport) {
      this.quoteManager.supportTicketService.disposeSession(this.branchQuoteSupport.id);
    }
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = undefined;
    }
    super.disconnectedCallback();
  }
  private _subscribeSnapshots(svc: SupportTicketService) {
    if (!svc) return;
    this._unsubscribe = svc.subscribe((state: SupportTicketsState, delta: SupportTicketsDelta) => {
      // Draft workflow event now only signals a draft session began (no adoption of persisted ticket yet)
      if (!this.branchQuoteSupport) return;
      if (delta.changedTicketIds.includes(this.branchQuoteSupport.id)) {
        const snap = state.tickets[this.branchQuoteSupport.id];
        if (snap?.ticket) {
          this.branchQuoteSupport = snap.ticket;
          this.dispatchUiChanged();
        }
      }
    });
  }

  // Map a support request type to a quote item type heuristic if required for adoption filtering.
  // For now, fallback returns undefined meaning we skip strict type comparison and adopt first available.
  // Extend this as the domain model clarifies itemType correlation.
  // Placeholder for future type-to-item correlation; currently not used after simplified adoption logic.

  get forceReadonly() {
    return this.quoteManager.isReadonly();
  }
  get branchQuote(): BranchQuote {
    return this.quoteManager.branchQuote;
  }
  get newEditor(): WebModuleBranchQuoteSupportNew | undefined | null {
    const e = this.ui.querySelector('#newEditor') ?? undefined;
    if (e instanceof WebModuleBranchQuoteSupportNew) return e;
    return undefined;
  }
  get ticketEditor(): WebModuleBranchQuoteConversationEditor | undefined {
    const e = this.ui.querySelector('#ticketeditor') ?? undefined;
    if (e instanceof WebModuleBranchQuoteConversationEditor) return e;
    return undefined;
  }

  override isDataReadonly(): boolean {
    // Layer 1 & 2: Resource locks and quote state (genuine readonly)
    const genuineReadonly = this.isGenuineReadonly;

    // Layer 3: Support ticket state (blocks content editing but allows reactivation)
    const ticketTerminalReadonly =
      this.branchQuoteSupport !== undefined &&
      (this.branchQuoteSupport?.status == BranchQuoteSupportStatus.Resolved ||
        this.branchQuoteSupport?.status == BranchQuoteSupportStatus.Cancelled);

    return genuineReadonly || ticketTerminalReadonly;
  }

  /**
   * Checks only genuine readonly conditions (resource locks + quote state).
   * Used for features like reactivation that should work even when ticket is in terminal state.
   */
  get isGenuineReadonly(): boolean {
    return super.isDataReadonly() || this.quoteManager.isReadonly();
  }

  protected isNew(): boolean {
    return !this.branchQuoteSupport || isEmptyOrSpace(this.branchQuoteSupport.recordVersion);
  }
  protected name() {
    return 'support-ticket';
  }
  protected abandonAndCloseButton(force?: boolean) {
    if (this.isDataReadonly()) return html``;
    return html` <webmodule-button size="small" variant="default" @click=${() => this.abandonAndClose(force)}>
      ${this.discardLabel()}
    </webmodule-button>`;
  }
  buttonMenu(): Snippet {
    // New ticket: provide Save Draft + Issue Ticket + Discard
    if (this.isNew()) {
      return html`${this.abandonAndCloseButton(true)}
        <webmodule-button size="small" variant="default" @click=${() => this._intentSaveDraft()}>
          ${LangBranchQuoteSupportNew.saveDraft()}
        </webmodule-button>
        <webmodule-button size="small" variant="primary" @click=${() => this._intentIssueTicket()}>
          ${LangBranchQuoteSupportNew.issueTicket()}
        </webmodule-button>`;
    }
    const ticket = this.branchQuoteSupport;
    const actions = ticket
      ? html`<support-ticket-actions
          .ticketId=${ticket.id}
          .quoteManager=${this.quoteManager as any}
          .readonly=${this.forceReadonly}
          .forceReadonly=${this.forceReadonly}
          hideWhenDisabled
          optimistic
          @support:intent:status-change=${(e: CustomEvent) => this._onStatusChangeIntent(e)}
          @support:intent:delete-draft=${() => this.abandonAndClose(true)}
        ></support-ticket-actions>`
      : html``;
    return html`${this.abandonAndCloseButton()} ${actions}`;
  }
  private ensureNewEditorDraftSync() {
    // Public sync from new editor component (typed API) – avoids any-cast hack.
    const editor = this.newEditor;
    editor?.syncDraftToService();
  }
  /** Intent: save draft for new ticket (unissued). */
  private async _intentSaveDraft() {
    const svc = this.quoteManager.supportTicketService;
    this.ensureNewEditorDraftSync();
    const errors = this._draftTicketId ? svc.validateDraftSession(this._draftTicketId) : [];
    if (errors.length) {
      await showValidations(errors);
      return;
    }
    await lockUIandExecute(async () => {
      if (!this._draftTicketId) return;
      const ok = await svc.saveTicket(this._draftTicketId, { finalizeDraftEntry: false });
      if (ok) {
        // Adopt newly persisted ticket
        const snap = svc.getState().tickets[this._draftTicketId];
        if (snap?.ticket) {
          this.branchQuoteSupport = snap.ticket;
          svc.beginMetadataEdit(this._draftTicketId);
          this.dispatchUiChanged();
        }
      }
    });
  }
  /** Intent: issue ticket (create then status->New if draft). */
  private async _intentIssueTicket() {
    const svc = this.quoteManager.supportTicketService;
    this.ensureNewEditorDraftSync();
    const errors = this._draftTicketId ? svc.validateDraftSession(this._draftTicketId) : [];
    if (errors.length) {
      await showValidations(errors);
      return;
    }
    await lockUIandExecute(async () => {
      if (!this._draftTicketId) return;
      const ok = await svc.saveTicket(this._draftTicketId, { finalizeDraftEntry: true });
      if (!ok) return;
      const snap = svc.getState().tickets[this._draftTicketId];
      if (snap?.ticket) {
        this.branchQuoteSupport = snap.ticket;
        this.requestUpdate();
        this.dispatchUiChanged();
      }
    });
  }
  /** Handle status change intent from actions component under UI lock */
  private async _onStatusChangeIntent(e: CustomEvent) {
    const detail = e.detail as {
      ticketId?: string; // required going forward
      nextStatus: BranchQuoteSupportStatus;
    };
    const svc = this.quoteManager.supportTicketService;
    const ticketId = detail.ticketId || this.branchQuoteSupport?.id || null; // slot-based fallback
    if (!ticketId) return;
    const state = svc.getState();
    const ticketSnap = state.tickets?.[ticketId];
    const ticket = ticketSnap?.ticket;
    const isNewUnpersisted = !ticket?.recordVersion || ticket.recordVersion === '';
    await lockUIandExecute(async () => {
      // Issue path for unpersisted new ticket
      if (isNewUnpersisted && detail.nextStatus === BranchQuoteSupportStatus.New) {
        // Ensure latest draft sync from new editor if still showing new view
        if (this.isNew()) this.ensureNewEditorDraftSync();
        const errors = this._draftTicketId ? svc.validateDraftSession(this._draftTicketId) : [];
        if (errors.length) {
          await showValidations(errors);
          return;
        }
        if (!this._draftTicketId) return;
        const ok = await svc.saveTicket(this._draftTicketId, { finalizeDraftEntry: true });
        if (!ok) return;
        const snap = svc.getState().tickets[this._draftTicketId];
        if (snap?.ticket && snap.ticket.status === BranchQuoteSupportStatus.Draft) {
          await svc.updateStatus({
            branchQuoteSupportId: snap.ticket.id,
            previousStatus: snap.ticket.status,
            status: BranchQuoteSupportStatus.New
          });
        }
        this.requestUpdate();
        this.dispatchUiChanged();
        return;
      }
      // Standard update path
      if (!ticket) return; // cannot update status without persisted ticket
      await svc.updateStatus({
        branchQuoteSupportId: ticketId,
        previousStatus: ticket.status,
        status: detail.nextStatus
      });
    });
  }
  discardLabel(): unknown {
    if (this.isDataReadonly()) return LangFranchiseeQuoteSupportTicketView.close();

    return this.isNew()
      ? LangFranchiseeQuoteSupportTicketView.discard()
      : LangFranchiseeQuoteSupportTicketView.discardChanges();
  }
  get quoteSetId() {
    return this.quoteManager.quote.quoteSetId;
  }
  get branchQuoteSupportId(): string {
    return this.branchQuoteSupport?.id ?? emptyGuid;
  }
  //true then this page is not valid, and should be assumed to be cancelled
  public isTab(): boolean {
    return true;
  }

  protected getValidationErrors(): string[] {
    const svc = this.quoteManager.supportTicketService;
    if (this.isNew()) return this._draftTicketId ? svc.validateDraftSession(this._draftTicketId) : [];
    if (!this.branchQuoteSupport) return [];
    return svc.validateDraftMetadata(this.branchQuoteSupport.id);
  }

  protected async revertChanges(): Promise<boolean> {
    return true;
  }
  async prepareForSave(): Promise<void> {
    if (this.forceReadonly) return;

    this.ensureNewEditorDraftSync();
  }

  public internalDataChanged(): boolean {
    if (this.isNew()) return true;
    if (!this.branchQuoteSupport) return false;
    const svc = this.quoteManager.supportTicketService;
    return svc.hasMetadataChanges(this.branchQuoteSupport.id) || !!svc.getEditingEntryId(this.branchQuoteSupport.id);
  }
  protected getCaption(): Snippet {
    if (this.isNew()) return LangFranchiseeQuoteSupportTicketView.newSupportRequest();

    return LangFranchiseeQuoteSupportTicketView.supportRequest();
  }

  protected async internalSaveData(): Promise<boolean> {
    const svc = this.quoteManager.supportTicketService;
    const errors = this.getValidationErrors();
    if (errors.length) {
      await showValidations(errors);
      return false;
    }

    if (this.isNew()) {
      if (!this._draftTicketId) return true; // no draft started
      const ok = await svc.saveTicket(this._draftTicketId, { finalizeDraftEntry: false });
      return ok;
    }
    // Editing existing ticket: use unified type-specific save (active slot must correspond to addRequestType)
    if (!this.branchQuoteSupport) return true;
    return svc.saveTicket(this.branchQuoteSupport.id, { finalizeDraftEntry: false });
  }
  get quoteId() {
    return this.quoteManager.quoteId;
  }

  protected addRequestTemplate() {
    return html`<wmview-branchquote-support-new
      id="newEditor"
      .quoteManager=${this.quoteManager as any}
      .supportType=${this.addRequestType}
      .supplierName=${this.quoteManager.supplierName}
      .draftTicketId=${this._draftTicketId || ''}
    ></wmview-branchquote-support-new>`;
  }
  protected editRequestTemplate() {
    return html`
      <wmview-branchquote-conversation-editor
        .quoteManager=${this.quoteManager as any}
        .readonly=${this.isReadonly}
        id="ticketeditor"
        .branchQuoteSupport=${this.branchQuoteSupport}
        .concurrency=${this.branchQuoteSupport?.recordVersion ?? ''}
        .supplierName=${this.quoteManager.supplierName}
      ></wmview-branchquote-conversation-editor>
    `;
  }
  private _didInitialConversationLoad = false;
  updated(changed: Map<string, unknown>): void {
    if (changed.has('branchQuoteSupport') && this.branchQuoteSupport && this.quoteManager) {
      const svc = this.quoteManager.supportTicketService;
      // Hydrate baseline metadata/edit context
      svc.ensureTicketHydrated(this.branchQuoteSupport);
      svc.beginMetadataEdit(this.branchQuoteSupport.id);
      // Perform a single initial conversation load if not already loaded in this component lifecycle
      if (!this._didInitialConversationLoad) {
        const id = this.branchQuoteSupport.id;
        const session = svc.getSession(id);
        if (!session.loaded) {
          // Allow failure to surface upstream (will be handled globally or by caller)
          void svc.loadConversation(id);
        }
        this._didInitialConversationLoad = true;
      }
    }
  }
  protected bodyTemplate(): EventTemplate {
    return this.isNew() ? this.addRequestTemplate() : this.editRequestTemplate();
  }

  /** Notification handler - no service work needed since editor calls service directly */
}
